import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  // TODO: migrate to @dnd-kit/react (new API with type/accept/group constraints)
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Column, Table } from '@tanstack/react-table';
import { Eye, EyeOff, GripVertical } from 'lucide-react';
import { useState } from 'react';

export default function TableColumnsOrderAndVisibilityList<TData>({
  table,
}: {
  table: Table<TData>;
}) {
  const columns = table.getAllColumns().filter((column) => column.getCanHide());
  const [items, setItems] = useState(() => {
    const { columnOrder } = table.getState();
    const columnMap = new Map(columns.map((col) => [col.id, col]));
    if (!columnOrder || !columnOrder.length) {
      return columns.map((column) => ({ id: column.id, column }));
    }
    return columnOrder
      .map((id) => {
        const column = columnMap.get(id);
        return column ? { id, column } : null;
      })
      .filter(
        (item): item is { id: string; column: Column<TData> } => item !== null,
      );
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          setItems((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            const newItems = arrayMove(items, oldIndex, newIndex);

            // Update the table's column order
            table.setColumnOrder(newItems.map((item) => item.id));

            return newItems;
          });
        }
      }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-1-">
          {items.map(({ id, column }) => (
            <DraggableColumnItem key={id} id={id} column={column} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function DraggableColumnItem<TData>({
  column,
  id,
}: {
  column: Column<TData>;
  id: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  const isVisible = column.getIsVisible();
  const label =
    typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex items-center gap-1', isDragging && 'opacity-50')}
    >
      <Button
        variant="ghost"
        className={cn(
          'flex-1 justify-between gap-2',
          !isVisible && 'text-muted-foreground',
        )}
        onClick={() => column.toggleVisibility()}
      >
        <span className="truncate">{label}</span>
        {isVisible ? (
          <Eye className="size-4 shrink-0" />
        ) : (
          <EyeOff className="size-4 shrink-0 opacity-50" />
        )}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        {...attributes}
        {...listeners}
        className="touch-none p-1 opacity-50 hover:opacity-100"
      >
        <GripVertical className="size-4" />
      </Button>
    </div>
  );
}
